import { Link } from "wouter";
import { ArrowRight, ClipboardCheck, FileSignature, ShieldCheck } from "lucide-react";
import { SEO } from "@/components/SEO";

const formCards = [
  {
    title: "Testal Setup Authorization",
    description: "Square setup, DoorDash Drive authorization, launch questionnaire, go-live test approval, and signed confirmation.",
    href: "/client-forms/testal-doordash",
    status: "Ready for Fernando",
    icon: FileSignature,
  },
  {
    title: "TG Notary Now Technical Questionnaire",
    description: "Booking, payments, Microsoft/Azure, Sumsub, Setasign/eSeal, record retention, Google Business, and AI phase-two questions.",
    href: "/client-forms/tg-notary-questionnaire",
    status: "Testing",
    icon: ClipboardCheck,
  },
];

export default function ClientFormsPage() {
  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#102033]">
      <SEO
        title="Client Forms"
        description="Secure Better Systems AI client forms, authorizations, and onboarding documents."
        image="https://bettersystems.ai/og-image.png"
        url="https://bettersystems.ai/client-forms"
        robots="noindex, nofollow"
      />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Manrope:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <main className="px-4 pb-20 pt-20 md:pt-24" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <section className="mx-auto max-w-6xl px-0 py-10 md:py-14">
          <div className="max-w-3xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#102033]/15 bg-white px-3 py-2 text-xs font-extrabold uppercase text-[#102033]/70">
              <ClipboardCheck className="h-4 w-4 text-[#F26B1F]" />
              Client operations
            </div>
            <h1
              className="mb-5 max-w-3xl text-4xl font-black leading-[1.05] text-[#102033] md:text-6xl"
              style={{ fontFamily: "'Archivo', sans-serif", letterSpacing: 0 }}
            >
              Better Systems AI client forms
            </h1>
            <p className="max-w-2xl text-lg leading-8 text-[#475569]">
              Use these forms when we need clear approval, setup details, and a clean record before making changes to a client system.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-0 py-0">
          <div className="grid gap-4 md:grid-cols-2">
            {formCards.map((form) => {
              const Icon = form.icon;
              return (
                <Link
                  key={form.href}
                  href={form.href}
                  className="group rounded-lg border border-[#102033]/12 bg-white p-6 shadow-[0_18px_50px_rgba(16,32,51,0.08)] transition hover:-translate-y-1 hover:border-[#F26B1F]/45 hover:shadow-[0_24px_70px_rgba(16,32,51,0.12)]"
                >
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#102033] text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="rounded-full bg-[#EAF7F0] px-3 py-1 text-xs font-extrabold uppercase text-[#176946]">
                      {form.status}
                    </span>
                  </div>
                  <h2 className="mb-2 text-2xl font-black text-[#102033]" style={{ fontFamily: "'Archivo', sans-serif" }}>
                    {form.title}
                  </h2>
                  <p className="mb-6 text-base leading-7 text-[#536276]">{form.description}</p>
                  <span className="inline-flex items-center text-sm font-extrabold uppercase text-[#F26B1F]">
                    Open form <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="mt-8 rounded-lg border border-[#102033]/12 bg-[#FFFDF8] p-5 text-sm leading-7 text-[#536276]">
            <div className="mb-2 flex items-center gap-2 font-extrabold text-[#102033]">
              <ShieldCheck className="h-5 w-5 text-[#F26B1F]" />
              Process note
            </div>
            Signed submissions are emailed to BSA and should also be saved into the client folder after submission.
          </div>
        </section>
      </main>
    </div>
  );
}
